import { API_CALL } from './actionConstants'
import { BOOK_ADDON, FETCH_ADDON_BOOKING_AVAILABILITY, FETCH_OFFER_PACKAGE_ADDONS } from './apiActionConstants'

import moment from 'moment'
import { groupBy, sum, arrayToObject } from 'lib/array/arrayUtils'
import config from 'constants/config'
import { bookAddons, updateAddon, getAddonAvailability, getOfferPackageAddons } from 'api/addons'
import type { AppAction } from './types/ActionTypes'

export function BookAddon(
  addons: Array<App.OrderAddonItem>,
  session: App.AddonAvailability,
  order: App.Order,
): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const customer = state.auth.account
    const packageAddonsById = arrayToObject(state.addon.addonsByPackage[order.items[0].package.id], a => a.idSalesforceExternal ?? a.id)
    const notBookedAddons = addons.filter(addon => !addon.channelBookingMade && !addon.booked)
    const costCurrency = packageAddonsById[notBookedAddons[0].idSalesforceExternal ?? notBookedAddons[0].id].costCurrency || 'AUD'
    const totalCost = sum(notBookedAddons, addon => packageAddonsById[addon.idSalesforceExternal ?? addon.id].price)

    const grouped = groupBy(notBookedAddons, addon => addon.channelFareType)
    const quantities = [...grouped.entries()].map(groupedAddons => {
      const addons = groupedAddons[1]
      const addon = addons[0]
      const addonIds = addons.map(addon => addon.id)
      const priceOption = session.priceOptions.find(option => option.label.toUpperCase() === addon.channelFareType?.toUpperCase())
      return {
        value: addons.length,
        fare_type: priceOption?.label,
        price_id: priceOption?.id,
        addon_items: addonIds,
      }
    })

    const addonsBookingNumbers = notBookedAddons.map(addon => addon.bookingNumber).join(',') || null

    const bookingData = {
      customer: {
        id: customer.memberId,
        first_name: customer.givenName,
        last_name: customer.surname,
        email: customer.email,
        phone: `+${customer.phonePrefix}${customer.phone}`,
      },
      order: {
        id: order.id,
        booking_number: order.bookingNumber,
      },
      items: {
        product_code: session.externalAddonId,
        booking_numbers: addonsBookingNumbers,
        start_time_local: session.startTimeLocal,
        quantities,
      },
      payment: {
        amount: totalCost,
        currency: costCurrency,
      },
    }

    if (addons[0].channelBookingChangeable) {
      dispatch({
        type: API_CALL,
        api: BOOK_ADDON,
        request: () => updateAddon({
          addon_item_id: addons[0].id,
          start_time_local: session.startTimeLocal,
        }).then(result => {
          const updatedItem = result.items[0]
          const updatedAddons = addons.map(addon => ({
            ...addon,
            channelBookingEndTime: updatedItem.end_time_local,
            channelBookingStartTime: updatedItem.start_time_local,
            channelBookingMade: true,
            channelBookingId: result.channel_booking_number,
          }))
          return updatedAddons
        }),
      })
    } else {
      dispatch({
        type: API_CALL,
        api: BOOK_ADDON,
        request: () => bookAddons(bookingData).then(result => {
          const updatedItem = result.items[0]
          const updatedAddons = addons.map(addon => ({
            ...addon,
            channelBookingEndTime: updatedItem.end_time_local,
            channelBookingStartTime: updatedItem.start_time_local,
            channelBookingMade: true,
            channelBookingId: result.channel_booking_number,
          }))
          return updatedAddons
        }),
      })
    }
  }
}

export function fetchAddonBookingAvailability(
  addon: App.OrderAddonItem,
  checkIn: string,
  checkOut: string,
  seats = 1,
): AppAction {
  return (dispatch) => {
    // We allow users to see/select extra days outside of check-in/out.
    const extraDays = config.ADDONS_REZDY_EXTRA_DAYS || 3
    const firstDay = moment(checkIn).subtract(extraDays, 'days').startOf('day').format('YYYY-MM-DD HH:mm:ss')
    const lastDay = moment(checkOut).add(extraDays, 'days').endOf('day').format('YYYY-MM-DD HH:mm:ss')

    const productCode = addon.channelProductId
    // `productCode` is necessary for `getAddonAvailability`
    if (!productCode) return

    dispatch({
      type: API_CALL,
      api: FETCH_ADDON_BOOKING_AVAILABILITY,
      request: () => getAddonAvailability({
        productCode,
        startTime: firstDay,
        endTime: lastDay,
        seats,
      }),
      addonId: addon.id,
    })
  }
}

export function fetchOfferPackageAddons(offerId: string, packageId: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const existingAddons = state.addon.addonsByPackage[packageId]
    if (existingAddons || state.addon.addonsByPackageLoading[packageId]) {
      // addons don't change all that often, don't load if we already have information for it
      // also don't re-fetch if we're already loading it!
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_OFFER_PACKAGE_ADDONS,
      request: () => getOfferPackageAddons(offerId, packageId, state.geo.currentRegionCode, state.geo.currentCurrency),
      packageId,
      offerId,
    })
  }
}
